import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSpaceConfig } from './context/SpaceConfigContext';

import ProtectedRoute from './auth/ProtectedRoute';
import Login from './pages/Login';
import ErrorBoundary from './components/common/ErrorBoundary';
import Loader from './components/common/Loader';
import { subscribe } from './utils/loadingState';

import Dashboard from './pages/Dashboard';
import TeleportMap from './pages/TeleportMap';
import HomeHeader from './components/homepage/HomeHeader';
import Sidebar from './components/homepage/Sidebar';
import Footer from './components/homepage/Footer';
import Booth from './pages/Booth';
import BoothEdit from './pages/BoothEdit';
import Recognition from './pages/Recognition';
import Portal from './pages/Portal';
import Charities from './pages/Charities';
import Settings from './pages/Settings';
import Payments from './pages/Payments';
import Rewards from './pages/Rewards';
import Events from './pages/Events';
import AIModeration from './pages/AIModeration';
import Lighting from './pages/Lighting';
import Selfies from './pages/Selfies';
import Items from './pages/Items';
import FreeTrialAddItems from './pages/FreeTrialAddItems';
import Packages from './pages/Packages';
import PackageAddItems from './pages/PackageAddItems';
import Subscriptions from './pages/Subscriptions';
import Advertisers from './pages/Advertisers';
import AdvertiserBrandPage from './pages/AdvertiserBrandPage';
import AdvertiserCampaignPage from './pages/AdvertiserCampaignPage';
import Hosting from './pages/Hosting';
import Approvals from './pages/Approvals';
import AdminPortal from './pages/AdminPortal';
import UserAnalytics from './pages/UserAnalytics';
import CcuSettings from './pages/CcuSettings';
import GameScoreBoard from './pages/GameScoreBoard';
import GameSchedule from './pages/GameSchedule';
import SpaceSelect from './pages/SpaceSelect';
import Unauthorized from './pages/Unauthorized';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import CookiePolicy from './pages/legal/CookiePolicy';
import TermsOfService from './pages/legal/TermsOfService';
import ContactSupport from './pages/legal/ContactSupport';
import DsaLegalInformation from './pages/legal/DsaLegalInformation';

function FeatureDisabled() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', textAlign: 'center' }}>
      <Box>
        <LockOutlinedIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>Feature Not Enabled</Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Please contact your administrator to enable this feature.
        </Typography>
      </Box>
    </Box>
  );
}

function FlagRoute({ flag, children }) {
  const { config, loading } = useSpaceConfig() ?? {};
  if (loading) return null;
  if (config && config[flag] === false) return <FeatureDisabled />;
  return children;
}

const HEADER_HEIGHT = 64;

function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(220);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  return (
    <Box sx={{ display: 'flex', height: ['100vh', '100dvh'] }}>
      <Sidebar
        onWidthChange={setSidebarWidth}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onClose={handleDrawerToggle}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        <HomeHeader
          sidebarWidth={isMobile ? 0 : sidebarWidth}
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: '#EAF0F6',
            overflow: 'auto',
            height: `calc(100dvh - ${HEADER_HEIGHT}px)`,
            ml: isMobile ? 0 : `${sidebarWidth}px`,
            mt: `${HEADER_HEIGHT}px`,
            transition: 'margin-left 0.2s ease',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flexGrow: 1, p: 1 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/booth" element={<FlagRoute flag="booths"><Booth /></FlagRoute>} />
              <Route path="/booth/:id" element={<FlagRoute flag="booths"><BoothEdit /></FlagRoute>} />
              <Route path="/recognition" element={<FlagRoute flag="recognition"><Recognition /></FlagRoute>} />
              <Route path="/ai-moderation" element={<FlagRoute flag="aiModeration"><AIModeration /></FlagRoute>} />
              {/* <Route path="/portal" element={<Portal />} /> */}
              <Route path="/events" element={<FlagRoute flag="events"><Events /></FlagRoute>} />
              <Route
                path="/teleport-map"
                element={
                  <DndProvider backend={HTML5Backend}>
                    <TeleportMap />
                  </DndProvider>
                }
              />
              <Route path="/items" element={<FlagRoute flag="items"><Items /></FlagRoute>} />
              <Route path="/items/free-trial/add" element={<FlagRoute flag="items"><FreeTrialAddItems /></FlagRoute>} />
              <Route path="/packages" element={<FlagRoute flag="items"><Packages /></FlagRoute>} />
              <Route path="/packages/:packageId/items" element={<FlagRoute flag="items"><PackageAddItems /></FlagRoute>} />
              <Route path="/subscriptions" element={<FlagRoute flag="subscriptions"><Subscriptions /></FlagRoute>} />
              <Route path="/rewards" element={<FlagRoute flag="rewards"><Rewards /></FlagRoute>} />
              <Route path="/charities" element={<FlagRoute flag="charities"><Charities /></FlagRoute>} />
              <Route path="/lighting" element={<FlagRoute flag="lighting"><Lighting /></FlagRoute>} />
              <Route path="/selfies" element={<FlagRoute flag="selfies"><Selfies /></FlagRoute>} />
              <Route path="/payments" element={<FlagRoute flag="payments"><Payments /></FlagRoute>} />
              <Route path="/advertisers" element={<FlagRoute flag="advertisers"><Advertisers /></FlagRoute>} />
              <Route path="/advertisers/:brandId" element={<FlagRoute flag="advertisers"><AdvertiserBrandPage /></FlagRoute>} />
              <Route path="/advertisers/:brandId/campaign/:campaignId" element={<FlagRoute flag="advertisers"><AdvertiserCampaignPage /></FlagRoute>} />
              {/* Hosting is now configured inline under Event (2D format) — standalone screen disabled for now. */}
              {/* <Route path="/hosting" element={<FlagRoute flag="hosting"><Hosting /></FlagRoute>} /> */}
              <Route path="/approvals" element={<FlagRoute flag="approvals"><Approvals /></FlagRoute>} />
              <Route path="/settings" element={<Settings />} />
              <Route
                path="/user-analytics"
                element={
                  <ProtectedRoute segment='/user-analytics'>
                    <UserAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute segment='/admin'>
                    <AdminPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ccu-settings"
                element={
                  <ProtectedRoute segment='/ccu-settings'>
                    <CcuSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/game-score-board"
                element={
                  <ProtectedRoute segment='/game-score-board'>
                    <GameScoreBoard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/game-schedule"
                element={
                  <ProtectedRoute segment='/game-schedule'>
                    <GameSchedule />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
}

function AppLoader({ loading }) {
  const location = useLocation();
  if (!loading || location.pathname === '/login') return null;
  return <Loader />;
}

function App() {
  const [loading, setLoading] = useState(false);

  useEffect(() => subscribe(setLoading), []);

  return (
    <Router>
      <CssBaseline />
      <AppLoader loading={loading} />
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/select-space" element={<ProtectedRoute><SpaceSelect /></ProtectedRoute>} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contact-support" element={<ContactSupport />} />
          <Route path="/dsa-legal-information" element={<DsaLegalInformation />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <AppLayout />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
